import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Row, Col } from 'react-bootstrap';
import { DealersClient, DealerDto } from '../web-api-client.ts';
import UserContext from '../providers/UserContext.js';
import BackButton from './BackButton.js';
import useToast from '../hooks/UseToast.js';

// ─── Hours: schema + helpers ─────────────────────────────────────────────────
// Stored as a JSON string on Dealer.Hours. Schema is always 7 entries in
// Mon→Sun order, each with: { day, open: "HH:mm" | null, close: "HH:mm" | null,
// closed: boolean }. Times are 24-hour (locale-independent) but rendered with
// the browser's <input type="time"> control which adapts to the user's locale.
const DAYS = [
  { key: 'Mon', label: 'Monday' },
  { key: 'Tue', label: 'Tuesday' },
  { key: 'Wed', label: 'Wednesday' },
  { key: 'Thu', label: 'Thursday' },
  { key: 'Fri', label: 'Friday' },
  { key: 'Sat', label: 'Saturday' },
  { key: 'Sun', label: 'Sunday' },
];

// Sensible starting hours when a dealer has never set them. Mirrors the
// most common US small-business pattern; the editor is the source of truth
// once the dealer saves anything.
const DEFAULT_HOURS = [
  { day: 'Mon', open: '09:00', close: '18:00', closed: false },
  { day: 'Tue', open: '09:00', close: '18:00', closed: false },
  { day: 'Wed', open: '09:00', close: '18:00', closed: false },
  { day: 'Thu', open: '09:00', close: '18:00', closed: false },
  { day: 'Fri', open: '09:00', close: '18:00', closed: false },
  { day: 'Sat', open: '10:00', close: '16:00', closed: false },
  { day: 'Sun', open: null, close: null, closed: true },
];

// Parse a stored JSON string back into a 7-entry array. Anything malformed
// or missing falls back to DEFAULT_HOURS — we'd rather show sensible defaults
// than crash the page.
function parseHours(json) {
  if (!json) return DEFAULT_HOURS.map(d => ({ ...d }));
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return DEFAULT_HOURS.map(d => ({ ...d }));
    // Map by day key so missing days fall back to default, and unknown
    // keys in the payload are dropped silently.
    const byDay = new Map(parsed.map(e => [e?.day, e]));
    return DAYS.map(({ key }) => {
      const e = byDay.get(key) ?? {};
      return {
        day: key,
        open: typeof e.open === 'string' ? e.open : null,
        close: typeof e.close === 'string' ? e.close : null,
        closed: !!e.closed,
      };
    });
  } catch {
    return DEFAULT_HOURS.map(d => ({ ...d }));
  }
}

function serializeHours(hours) {
  // Normalize: closed days drop open/close to keep the payload clean.
  const clean = hours.map(h => h.closed
    ? { day: h.day, open: null, close: null, closed: true }
    : { day: h.day, open: h.open || null, close: h.close || null, closed: false });
  return JSON.stringify(clean);
}

const HoursEditor = ({ value, onChange }) => {
  const update = (idx, patch) => {
    const next = value.map((h, i) => (i === idx ? { ...h, ...patch } : h));
    onChange(next);
  };

  // Quick action: copy a row's hours to all *later* days in the list. Skips
  // closed days unless the source row is itself closed (in which case it
  // marks all subsequent days closed too).
  const copyDown = (idx) => {
    const src = value[idx];
    const next = value.map((h, i) => {
      if (i <= idx) return h;
      return src.closed
        ? { ...h, closed: true, open: null, close: null }
        : { ...h, closed: false, open: src.open, close: src.close };
    });
    onChange(next);
  };

  // Quick action: stamp Mon-Fri with the first row's hours so dealers don't
  // have to repeat themselves five times for a typical weekday schedule.
  const applyWeekdays = () => {
    const src = value[0];
    const next = value.map((h, i) => {
      if (i > 4) return h; // Sat/Sun untouched
      return src.closed
        ? { ...h, closed: true, open: null, close: null }
        : { ...h, closed: false, open: src.open, close: src.close };
    });
    onChange(next);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0">Hours of Operation</Form.Label>
        <Button
          variant="outline-secondary"
          size="sm"
          type="button"
          onClick={applyWeekdays}
          title="Copy Monday's hours to Tuesday–Friday"
        >
          Apply Monday to Mon–Fri
        </Button>
      </div>

      <div className="border rounded p-2">
        {value.map((h, idx) => {
          const dayLabel = DAYS.find(d => d.key === h.day)?.label ?? h.day;
          return (
            <Row key={h.day} className="align-items-center g-2 py-1">
              <Col xs={4} sm={3} md={2}>
                <span className="fw-semibold">{dayLabel}</span>
              </Col>
              <Col xs="auto">
                <Form.Check
                  type="switch"
                  id={`closed-${h.day}`}
                  label="Closed"
                  checked={h.closed}
                  onChange={(e) => update(idx, {
                    closed: e.target.checked,
                    // Preserve previously-entered times when toggling so the
                    // user doesn't lose them on accidental clicks.
                    open: e.target.checked ? null : (h.open || '09:00'),
                    close: e.target.checked ? null : (h.close || '17:00'),
                  })}
                />
              </Col>
              <Col xs="auto">
                <Form.Control
                  type="time"
                  size="sm"
                  value={h.open ?? ''}
                  disabled={h.closed}
                  onChange={(e) => update(idx, { open: e.target.value })}
                  style={{ width: '7.5rem' }}
                />
              </Col>
              <Col xs="auto" className="text-muted small">to</Col>
              <Col xs="auto">
                <Form.Control
                  type="time"
                  size="sm"
                  value={h.close ?? ''}
                  disabled={h.closed}
                  onChange={(e) => update(idx, { close: e.target.value })}
                  style={{ width: '7.5rem' }}
                />
              </Col>
              <Col xs="auto" className="ps-2">
                {idx < value.length - 1 && (
                  <Button
                    variant="link"
                    size="sm"
                    type="button"
                    className="p-0 text-muted text-decoration-none"
                    onClick={() => copyDown(idx)}
                    title="Copy these hours to all later days"
                  >
                    Copy to days below ↓
                  </Button>
                )}
              </Col>
            </Row>
          );
        })}
      </div>
      <div className="form-text small">
        Times use 24-hour format internally but display in your browser's
        locale. Toggle "Closed" for any day the dealership isn't open.
      </div>
    </div>
  );
};

/**
 * Setup → Dealer Info
 *
 * Lets a dealer admin edit their own dealership's display info — the fields
 * that show up on window stickers (name, slogan, phone, website, address)
 * plus the logo URL.
 *
 * Auth:
 *   - Gated in NavMenu to users with the "Dealer Admin" role
 *   - Also re-checked here so a direct-URL hit by a non-admin renders a
 *     friendly message instead of a confusing empty form
 *   - The backend PUT /api/Dealers/{id} also enforces role + ownership
 */
const DealerInfo = () => {
  const user = useContext(UserContext);
  const { showToast } = useToast();

  const isDealerAdmin = !!(user?.roles && user.roles.includes('Dealer Admin'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    logoUri: '',
    slogan: '',
    phone: '',
    website: '',
    address: '',
    email: '',
  });

  // Hours kept separate from formData because it's structured (array) rather
  // than a flat string — keeps handleChange simple and lets HoursEditor own
  // its own value/onChange contract.
  const [hours, setHours] = useState(() => parseHours(null));

  // Cache-buster appended to the logo preview URL so a freshly-uploaded blob
  // displays immediately even when the underlying URL is unchanged (e.g.
  // when the user re-uploads a different PNG over the same blob path).
  const [logoCacheKey, setLogoCacheKey] = useState(0);

  // ── Fetch current dealer info on mount ─────────────────────────────────────
  useEffect(() => {
    if (!isDealerAdmin) {
      setLoading(false);
      return;
    }

    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const client = new DealersClient();
        const dto = await client.getDealerInfo(user?.dealerId ?? null);
        if (!active) return;
        setFormData({
          id: dto.id ?? null,
          name: dto.name ?? '',
          logoUri: dto.logoUri ?? '',
          slogan: dto.slogan ?? '',
          phone: dto.phone ?? '',
          website: dto.website ?? '',
          address: dto.address ?? '',
          email: dto.email ?? '',
        });
        setHours(parseHours(dto.hours ?? null));
      } catch (e) {
        console.error(e);
        if (active) setError('Failed to load dealer info.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [isDealerAdmin, user?.dealerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    // Always reset the input so the same file can be re-selected later
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (!formData.id) {
      setError('Cannot upload: dealer id is missing.');
      return;
    }

    // Client-side guard. The backend validates again.
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setError(`Unsupported file type "${file.type}". Use PNG, JPG, WEBP, GIF, or SVG.`);
      return;
    }

    setUploadingLogo(true);
    setError(null);
    try {
      // Use raw fetch with FormData — simpler than threading types through
      // the generated client, and matches the pattern used by DealersAdmin.
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/Dealers/${formData.id}/logo`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Upload failed');
      }
      const dto = await res.json();
      setFormData((prev) => ({ ...prev, logoUri: dto.logoUri ?? '' }));
      setLogoCacheKey((k) => k + 1);
      showToast({ message: 'Logo updated.', type: 'success' });
    } catch (e) {
      console.error(e);
      setError('Failed to upload logo. Please try a different file.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!formData.id) {
      setError('Cannot delete: dealer id is missing.');
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Remove the current logo? This cannot be undone.')) return;

    setDeletingLogo(true);
    setError(null);
    try {
      const client = new DealersClient();
      await client.deleteOwnLogo(formData.id);
      setFormData((prev) => ({ ...prev, logoUri: '' }));
      setLogoCacheKey((k) => k + 1);
      showToast({ message: 'Logo removed.', type: 'success' });
    } catch (e) {
      console.error(e);
      setError('Failed to remove logo.');
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.id) {
      setError('Cannot save: dealer id is missing.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const client = new DealersClient();
      const dto = new DealerDto({
        id: formData.id,
        name: formData.name,
        logoUri: formData.logoUri,
        slogan: formData.slogan,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        email: formData.email,
        hours: serializeHours(hours),
      });
      await client.updateOwnDealer(formData.id, dto);
      showToast({ message: 'Dealer info updated.', type: 'success' });
    } catch (e) {
      console.error(e);
      setError('Failed to save dealer info. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Access denied (defensive — nav link is already gated) ─────────────────
  if (!isDealerAdmin) {
    return (
      <Card className="p-4">
        <BackButton />
        <h2>Dealer Info</h2>
        <Alert variant="warning" className="mt-3">
          You don't have permission to view this page. Dealer admin access is required.
        </Alert>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="p-4">
        <BackButton />
        <h2>Dealer Info</h2>
        <div className="d-flex align-items-center gap-2 mt-3">
          <Spinner size="sm" animation="border" /> Loading…
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <BackButton />
      <h2>Dealer Info</h2>
      <p className="text-muted">
        This is the contact and branding information that appears on your window stickers and QR sheets.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Dealership Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Slogan <span className="text-muted small">(optional)</span></Form.Label>
          <Form.Control
            type="text"
            name="slogan"
            value={formData.slogan}
            onChange={handleChange}
            placeholder="Your Company's Slogan Goes Here"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="sales@example.com"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Website</Form.Label>
          <Form.Control
            type="url"
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://www.example.com"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="123 Main St, City, State 12345"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <HoursEditor value={hours} onChange={setHours} />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Logo</Form.Label>
          <div className="d-flex align-items-start gap-3 flex-wrap">
            {/* Preview pane — shows the current logo (or a placeholder).
                logoCacheKey appended as ?v= forces the browser to refetch the
                blob after an upload even when the URL string is identical. */}
            <div
              className="border rounded d-flex align-items-center justify-content-center"
              style={{
                background: '#f8f9fa',
                width: '220px',
                height: '120px',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {formData.logoUri ? (
                <img
                  src={`${formData.logoUri}${formData.logoUri.includes('?') ? '&' : '?'}v=${logoCacheKey}`}
                  alt="Logo preview"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span className="text-muted small">No logo uploaded</span>
              )}
            </div>

            <div className="d-flex flex-column gap-2">
              <div className="text-muted small">
                PNG, JPG, WEBP, GIF, or SVG. Recommended: transparent background,
                roughly 600&times;200 px or square.
              </div>

              {/* Hidden native input; visible button triggers it for nicer UX */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                style={{ display: 'none' }}
                onChange={handleFileSelected}
              />

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo || deletingLogo}
                >
                  {uploadingLogo ? 'Uploading…' : (formData.logoUri ? 'Replace Logo' : 'Upload Logo')}
                </Button>

                {formData.logoUri && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleDeleteLogo}
                    disabled={uploadingLogo || deletingLogo}
                  >
                    {deletingLogo ? 'Removing…' : 'Remove'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Form>
    </Card>
  );
};

export default DealerInfo;
